'use client';

import { useState, useEffect } from 'react';
import { ArrowDown, Github, Linkedin, Instagram, Facebook, MessageCircle, Mail, Eye } from 'lucide-react';
import { PORTFOLIO_STATS } from '@/data/portfolioData';
import { useCVModal } from '../context/CVModalContext';

const ROLES = [
  'Software Engineer',
  'Web Developer',
  'Backend Developer',
  'Problem Solver',
];

const SOCIAL_LINKS = [
  { icon: Github, href: 'https://github.com/Marwan-Hussein', label: 'GitHub' },
  { icon: Linkedin, href: 'https://www.linkedin.com/in/marwanhussein9', label: 'LinkedIn' },
  { icon: MessageCircle, href: 'https://wa.me/qr/BQP37VAJ5RB4J1', label: 'WhatsApp' },
  { icon: Instagram, href: 'https://www.instagram.com/marwan_hussein9?igsh=MWNzejU5em9hZDIyYw==', label: 'Instagram' },
  { icon: Facebook, href: 'https://www.facebook.com/share/1JALCAMUu4/', label: 'Facebook' },
];

function TypewriterText({ texts }: { texts: string[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const target = texts[currentIndex];
    let timeout: ReturnType<typeof setTimeout>;

    if (isPaused) {
      timeout = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, 2200);
    } else if (isDeleting) {
      if (currentText.length === 0) {
        setIsDeleting(false);
        setCurrentIndex((prev) => (prev + 1) % texts.length);
      } else {
        timeout = setTimeout(() => {
          setCurrentText((prev) => prev.slice(0, -1));
        }, 35);
      }
    } else {
      if (currentText.length === target.length) {
        setIsPaused(true);
      } else {
        timeout = setTimeout(() => {
          setCurrentText(target.slice(0, currentText.length + 1));
        }, 75);
      }
    }

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, isPaused, currentIndex, texts]);

  return (
    <span className="text-sand font-display font-600 italic">
      {currentText}
      <span className="text-sand/60 ml-0.5" style={{ animation: 'blink 1s step-end infinite' }}>|</span>
    </span>
  );
}

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const { openCVModal } = useCVModal();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const scrollToAbout = () => {
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
  };

  const reveal = (delay: string) =>
    `transition-all duration-900 ease-organic ${delay} ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
    }`;

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden pt-20 pb-16"
    >
      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-16 items-center">
          {/* Text — asymmetric left column */}
          <div className="lg:col-span-7 xl:col-span-6">
            <div className={reveal('delay-0')}>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sage/25 bg-sage/8 text-sage text-xs font-mono tracking-wide mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-sage animate-pulse" />
                Available for opportunities
              </span>
            </div>

            <div className={reveal('delay-100')}>
              <p className="font-mono text-parchment-muted text-xs tracking-[0.2em] uppercase mb-4">
                Hello — I&apos;m
              </p>
              <h1 className="font-display text-[clamp(2.75rem,7vw,5.5rem)] font-800 leading-[0.95] text-parchment mb-2">
                Marwan
              </h1>
              <h1 className="font-display text-[clamp(2.75rem,7vw,5.5rem)] font-800 leading-[0.95] gradient-text italic mb-6">
                Hussein
              </h1>
            </div>

            <div className={`text-xl sm:text-2xl mb-8 min-h-[2rem] ${reveal('delay-200')}`}>
              <TypewriterText texts={ROLES} />
            </div>

            <p className={`text-parchment-muted text-lg leading-relaxed max-w-lg mb-10 ${reveal('delay-300')}`}>
              Computer Science student at the Faculty of Computers and AI, crafting
              thoughtful software with Django, Python, and modern web technologies.
            </p>

            <div className={`flex flex-wrap gap-3 mb-12 ${reveal('delay-400')}`}>
              <button
                type="button"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-primary"
              >
                <Mail size={16} />
                Get in touch
              </button>
              <button type="button" onClick={openCVModal} className="btn-ghost">
                <Eye size={16} />
                View CV
              </button>
              <a
                href="https://github.com/Marwan-Hussein"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost border-parchment/10 text-parchment-muted hover:text-parchment"
              >
                <Github size={16} />
                GitHub
              </a>
            </div>

            <div className={`flex items-center gap-4 ${reveal('delay-500')}`}>
              <span className="text-parchment-muted/50 text-xs font-mono">Elsewhere</span>
              <div className="flex items-center gap-2">
                {SOCIAL_LINKS.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="p-2.5 rounded-lg border border-parchment/8 text-parchment-muted hover:text-sand hover:border-sand/25 hover:bg-sand/5 transition-all duration-300"
                    >
                      <Icon size={15} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Portrait — offset right column */}
          <div className={`lg:col-span-5 xl:col-span-6 flex justify-center lg:justify-end ${reveal('delay-300')}`}>
            <div className="relative">
              <div className="absolute -top-6 -left-8 w-32 h-32 border border-sand/15 rounded-full" />
              <div className="absolute -bottom-4 -right-6 w-20 h-20 bg-sand/8 rounded-full" />

              <div className="relative w-72 h-80 sm:w-80 sm:h-96 xl:w-96 xl:h-[28rem]">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-sand/15 to-clay/10 rotate-3" />
                <div className="absolute inset-0 rounded-2xl overflow-hidden border border-parchment/10 shadow-warm-lg -rotate-1">
                  <img
                    src="/assets/images/formal_AI_photo-1773581062271.png"
                    alt="Marwan Hussein — Software Engineer"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent" />
                </div>

                <div className="absolute -bottom-4 -left-6 warm-card rounded-xl px-4 py-3 flex items-center gap-3 shadow-warm animate-float">
                  <span className="w-2 h-2 rounded-full bg-sage animate-pulse" />
                  <span className="font-mono text-xs text-parchment/80">Open to work</span>
                </div>

                <div
                  className="absolute top-8 -right-8 warm-card rounded-xl px-4 py-3 shadow-warm animate-float"
                  style={{ animationDelay: '-3s' }}
                >
                  <div className="font-display text-2xl font-700 text-sand">{PORTFOLIO_STATS.projects}</div>
                  <div className="font-mono text-[10px] text-parchment-muted uppercase tracking-wider">Projects</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-6 lg:left-10 xl:left-16 flex items-center gap-3">
          <button
            type="button"
            onClick={scrollToAbout}
            className="group flex items-center gap-3 text-parchment-muted hover:text-sand transition-colors duration-300"
            aria-label="Scroll to about section"
          >
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Scroll</span>
            <span className="p-2 rounded-full border border-parchment/10 group-hover:border-sand/30 transition-all duration-300 group-hover:translate-y-0.5">
              <ArrowDown size={14} />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}
